// HIPAA seat bundle — two pieces:
//
//  • HipaaTrainingGate  — a BLOCKING overlay shown to a SEAT who hasn't yet
//    completed the agency's HIPAA training + signed the attestation for the
//    current version. They watch the video, read the agreement, tick "I have
//    read and understood", type their full name (e-signature) and accept. Until
//    then they can't use the CRM.
//
//  • HipaaSeatBundlePanel — the agency owner/agent configures the training
//    (paste the purchased video link + the attestation text reviewed by their
//    counsel, enable it, bump the version to force a re-train) and sees every
//    seat's completion status (the audit record).
//
// IMPORTANT (compliance): the attestation/training TEXT is the agency's own,
// reviewed by their HIPAA counsel — the app never generates HIPAA policy. The
// default text below is a clearly-marked DRAFT placeholder to be replaced.

import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Lock, Check, X, Clock, AlertTriangle, Upload, FileText, ExternalLink } from 'lucide-react';
import DateField from './DateField.jsx';
import { getHipaaStatus, ackHipaa, listHipaaAcks, getAgencySettings, updateAgencySettings, getHipaaConfig, getHipaaPlatformDefault, saveHipaaPlatformDefault, uploadHipaaCert, getHipaaCertUrl } from '../api.js';

// Turn a YouTube / Vimeo URL into an embeddable iframe src; null for anything
// else (then we show a "watch" link). YouTube/Vimeo are already on the CSP
// frame-src allowlist.
function embedUrl(url) {
  const s = String(url || '').trim();
  if (!s) return null;
  let m = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  m = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

const DRAFT_AGREEMENT_ES = `[BORRADOR — reemplazar con el texto aprobado por tu abogada HIPAA]

Como asistente (seat) con acceso a información médica protegida (PHI) de los clientes de esta agencia, confirmo que:

1. Manejaré la información de los pacientes de forma confidencial y solo para las tareas que me fueron asignadas (principio de "mínimo necesario").
2. No compartiré, copiaré ni divulgaré PHI fuera de Deskte ni a personas no autorizadas.
3. Protegeré mis credenciales de acceso y cerraré sesión en equipos compartidos.
4. Reportaré de inmediato cualquier acceso indebido o sospecha de filtración.
5. Entiendo que el acceso queda registrado y auditado, y que el incumplimiento puede tener consecuencias legales.`;

const DRAFT_AGREEMENT_EN = `[DRAFT — replace with the text approved by your HIPAA counsel]

As an assistant (seat) with access to clients' protected health information (PHI) at this agency, I confirm that:

1. I will handle patient information confidentially and only for the tasks assigned to me ("minimum necessary" principle).
2. I will not share, copy or disclose PHI outside Deskte or to unauthorized people.
3. I will protect my access credentials and log out on shared devices.
4. I will immediately report any improper access or suspected breach.
5. I understand that access is logged and audited, and that non-compliance may carry legal consequences.`;

// The placeholder draft in the UI language (the real text is the agency's own).
const draftAgreement = (es) => (es ? DRAFT_AGREEMENT_ES : DRAFT_AGREEMENT_EN);

// Informational "why this matters" + step-by-step instructions. These are
// generic/educational (NOT legal policy), so they ship as editable defaults.
const DRAFT_WHY_ES = `La capacitación HIPAA es obligatoria para cualquier persona que maneje información médica protegida (PHI) de los clientes. Te enseña a proteger esa información, evitar filtraciones y cumplir con la ley. Completarla protege a los clientes, a la agencia y a ti — y deja constancia de que estás capacitado/a.`;
const DRAFT_WHY_EN = `HIPAA training is required for anyone who handles clients' protected health information (PHI). It teaches you how to keep that information safe, avoid breaches and comply with the law. Completing it protects the clients, the agency and you — and creates a record that you are trained.`;

const DRAFT_STEPS_ES = `1. Abre el curso con el botón de abajo y complétalo.
2. Al terminar, descarga o guarda tu certificado (PDF o foto).
3. Sube aquí tu certificado.
4. Lee el acuerdo de confidencialidad, marca la casilla y firma con tu nombre.`;
const DRAFT_STEPS_EN = `1. Open the course with the button below and complete it.
2. When you finish, download or save your certificate (PDF or photo).
3. Upload your certificate here.
4. Read the confidentiality agreement, tick the box and sign with your name.`;

const draftWhy = (es) => (es ? DRAFT_WHY_ES : DRAFT_WHY_EN);
const draftSteps = (es) => (es ? DRAFT_STEPS_ES : DRAFT_STEPS_EN);

const inp = { width: '100%', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem', color: 'var(--c-white,#eef6ff)', fontSize: '.85rem', boxSizing: 'border-box', fontFamily: 'inherit' };
const lbl = { fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 };

// ── Seat blocking gate ───────────────────────────────────────────────────────
export function HipaaTrainingGate({ currentUser, lang = 'es' }) {
  const es = lang === 'es';
  const [status, setStatus] = useState(null); // { required, videoUrl, courseUrl, agreementText, version }
  const [read, setRead] = useState(false);
  const [name, setName] = useState(currentUser?.name || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [cert, setCert] = useState(null);       // { blobUrl, name } once uploaded
  const [uploading, setUploading] = useState(false);
  const [completedDate, setCompletedDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; });
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getHipaaStatus().then((d) => { if (alive) setStatus(d); }).catch(() => { if (alive) setStatus({ required: false }); });
    return () => { alive = false; };
  }, []);

  if (done || !status || !status.required) return null;

  const onPickCert = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setUploading(true);
    try { setCert(await uploadHipaaCert(file)); }
    catch (e2) { setErr(e2.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const needCert = status.requireCert !== false;
  const accept = async () => {
    setErr(null);
    if (needCert && !cert) { setErr(es ? 'Sube tu certificado del curso para continuar.' : 'Upload your course certificate to continue.'); return; }
    if (!read) { setErr(es ? 'Marca la casilla de que leíste y entiendes.' : 'Tick the box that you have read and understood.'); return; }
    if (name.trim().length < 2) { setErr(es ? 'Escribe tu nombre completo para firmar.' : 'Type your full name to sign.'); return; }
    if (!completedDate) { setErr(es ? 'Indica la fecha en que completaste el curso.' : 'Enter the date you completed the course.'); return; }
    setSaving(true);
    try { await ackHipaa(name.trim(), es ? 'es' : 'en', cert, completedDate); setDone(true); }
    catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // Preview the renewal date from the completion date + the agency's renew months.
  const renewMonths = Number.isFinite(status.renewMonths) ? status.renewMonths : 12;
  const expiryPreview = (() => {
    if (!completedDate || renewMonths <= 0) return null;
    const [y, m, d] = completedDate.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12));
    dt.setUTCMonth(dt.getUTCMonth() + renewMonths);
    return dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  })();

  const why = status.whyText || draftWhy(es);
  const steps = status.instructions || draftSteps(es);
  // The seat opens the course in their system language (fallback to the other).
  const courseLink = (es ? (status.courseUrlEs || status.courseUrl) : (status.courseUrl || status.courseUrlEs)) || '';
  // The seat watches the video in their system language (fallback to the other).
  const video = (es ? (status.videoUrlEs || status.videoUrl) : (status.videoUrl || status.videoUrlEs)) || '';
  const emb = embedUrl(video);
  const canSubmit = (!needCert || !!cert) && read && name.trim().length >= 2 && !saving;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(4,10,20,.92)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '4vh 16px' }}>
      <div style={{ width: '100%', maxWidth: 720, background: 'var(--c-surface,#0d1f33)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.5)' }}>
        <div style={{ background: 'linear-gradient(135deg,#0e7490,#155e75)', padding: '1.1rem 1.3rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShieldCheck size={22} style={{ color: '#fff' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem' }}>{es ? 'Capacitación HIPAA requerida' : 'HIPAA training required'}</div>
            <div style={{ color: 'rgba(255,255,255,.8)', fontSize: '.74rem' }}>{es ? 'Complétala una vez para poder usar el sistema.' : 'Complete it once to start using the system.'}</div>
          </div>
        </div>
        <div style={{ padding: '1.3rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Why this matters */}
          <div style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.7rem .8rem', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {why}
          </div>

          {/* Step-by-step instructions */}
          <div>
            <label style={lbl}>{es ? 'Pasos a seguir' : 'Steps to follow'}</label>
            <div style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{steps}</div>
          </div>

          {/* Take the course (external) — in the seat's language */}
          {courseLink && (
            <a href={courseLink} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--c-cyan,#00d4ff)', color: '#0a1628', fontWeight: 800, padding: '.7rem 1rem', borderRadius: 10, textDecoration: 'none', justifyContent: 'center' }}>
              <ExternalLink size={16} /> {es ? 'Abrir el curso de capacitación' : 'Open the training course'}
            </a>
          )}

          {/* Optional video (if the agency also pasted one) */}
          {video && (
            emb ? (
              <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--c-border,#1a3550)' }}>
                <iframe src={emb} title="HIPAA training" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }} />
              </div>
            ) : (
              <a href={video} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--c-surface3,#162d44)', color: 'var(--c-cyan,#00d4ff)', fontWeight: 700, padding: '.6rem 1rem', borderRadius: 10, textDecoration: 'none', justifyContent: 'center' }}>
                ▶ {es ? 'Ver el video' : 'Watch the video'}
              </a>
            )
          )}

          {/* Certificate upload */}
          {needCert && (
            <div>
              <label style={lbl}>{es ? 'Sube tu certificado del curso' : 'Upload your course certificate'}</label>
              <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onPickCert} style={{ display: 'none' }} />
              {cert ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,255,136,.08)', border: '1px solid rgba(0,255,136,.3)', borderRadius: 8, padding: '.6rem .8rem', fontSize: '.82rem', color: 'var(--c-green,#00ff88)' }}>
                  <Check size={16} /> <span style={{ flex: 1, color: 'var(--c-text,#c8dff0)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cert.name}</span>
                  <button onClick={() => setCert(null)} style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', display: 'flex' }}><X size={15} /></button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'center', background: 'var(--c-surface2,#112538)', border: '1px dashed var(--c-border,#1a3550)', color: 'var(--c-text2,#7da0c4)', borderRadius: 8, padding: '.7rem 1rem', fontSize: '.82rem', fontWeight: 700, cursor: uploading ? 'wait' : 'pointer' }}>
                  <Upload size={16} /> {uploading ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Elegir archivo (PDF o foto)' : 'Choose file (PDF or photo)')}
                </button>
              )}
            </div>
          )}

          {/* Agreement */}
          <div>
            <label style={lbl}>{es ? 'Acuerdo de confidencialidad (HIPAA)' : 'Confidentiality agreement (HIPAA)'}</label>
            <div style={{ maxHeight: 220, overflowY: 'auto', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.7rem .8rem', fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
              {status.agreementText || draftAgreement(es)}
            </div>
          </div>

          {/* Completion date → drives the renewal date (like a license) */}
          <div>
            <label style={lbl}>{es ? 'Fecha en que completaste el curso' : 'Date you completed the course'}</label>
            <DateField value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} style={inp} />
            {expiryPreview && (
              <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: 4 }}>
                {es ? `Tu capacitación vencerá el ${expiryPreview} (debes renovarla cada ${renewMonths} meses).` : `Your training will expire on ${expiryPreview} (renew every ${renewMonths} months).`}
              </div>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', cursor: 'pointer' }}>
            <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} style={{ width: 17, height: 17, marginTop: 1, accentColor: '#00d4ff', cursor: 'pointer' }} />
            <span>{es ? 'Completé el curso, leí el acuerdo y lo entiendo.' : 'I completed the course, read the agreement and understand it.'}</span>
          </label>
          <div>
            <label style={lbl}>{es ? 'Firma (escribe tu nombre completo)' : 'Signature (type your full name)'}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={es ? 'Nombre y apellido' : 'First and last name'} style={inp} />
          </div>

          {err && <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 7, padding: '.55rem .8rem' }}>{err}</div>}
          <button onClick={accept} disabled={!canSubmit}
            style={{ background: canSubmit ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface3,#162d44)', color: canSubmit ? '#0a1628' : 'var(--c-text3,#45688a)', border: 'none', borderRadius: 10, padding: '.75rem 1rem', fontWeight: 800, fontSize: '.9rem', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Lock size={16} /> {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Aceptar y continuar' : 'Accept and continue')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Owner/agent config + audit panel ─────────────────────────────────────────
// Two modes:
//  • Platform owner (Lissette as Deskte): edits the GENERIC default that every
//    agency inherits.
//  • Agency owner/agent: inherits the generic default; can "Personalizar" to
//    keep their own (override). When inheriting, the fields are read-only.
export default function HipaaSeatBundlePanel({ lang = 'es', isPlatformOwner = false }) {
  const es = lang === 'es';
  const [cfg, setCfg] = useState(null); // { enabled, videoUrl, agreementText, version, renewMonths }
  const [overriding, setOverriding] = useState(false); // agency mode: using own config?
  const [seats, setSeats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const loadSeats = () => listHipaaAcks().then((d) => setSeats(d?.seats || [])).catch(() => {});
  const norm = (h) => ({ enabled: h?.enabled === true, videoUrl: h?.videoUrl || '', videoUrlEs: h?.videoUrlEs || '', courseUrl: h?.courseUrl || '', courseUrlEs: h?.courseUrlEs || '', whyText: h?.whyText || '', instructions: h?.instructions || '', requireCert: h?.requireCert !== false, agreementText: h?.agreementText || '', version: Number(h?.version) || 1, renewMonths: Number.isFinite(h?.renewMonths) ? h.renewMonths : 12 });
  useEffect(() => {
    if (isPlatformOwner) {
      getHipaaPlatformDefault().then((d) => setCfg(norm(d))).catch(() => setCfg(norm({})));
    } else {
      getHipaaConfig().then(({ config, overriding }) => { setOverriding(!!overriding); setCfg(norm(config)); }).catch(() => setCfg(norm({})));
    }
    loadSeats();
  }, []);

  if (!cfg) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--c-text3,#45688a)' }}>{es ? 'Cargando…' : 'Loading…'}</div>;

  // Agency owners can edit only when they've chosen to customize; the platform
  // owner always edits the generic default.
  const editable = isPlatformOwner || overriding;
  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const persist = async (next) => {
    const payload = {
      enabled: !!next.enabled,
      videoUrl: next.videoUrl || '', videoUrlEs: next.videoUrlEs || '',
      courseUrl: next.courseUrl || '', courseUrlEs: next.courseUrlEs || '', whyText: next.whyText || '', instructions: next.instructions || '',
      requireCert: next.requireCert !== false,
      agreementText: next.agreementText || '',
      version: next.version, renewMonths: Number(next.renewMonths) || 0,
    };
    if (isPlatformOwner) {
      await saveHipaaPlatformDefault(payload);
    } else {
      await updateAgencySettings({ hipaaTraining: { override: true, ...payload } });
      setOverriding(true);
    }
  };
  const save = async (bumpVersion) => {
    setSaving(true); setErr(null); setOkMsg(null);
    try {
      const next = { ...cfg, version: bumpVersion ? (Number(cfg.version) || 1) + 1 : (Number(cfg.version) || 1) };
      await persist(next);
      setCfg(next);
      setOkMsg(bumpVersion ? (es ? 'Guardado. Se subió la versión: los seats deberán capacitarse de nuevo.' : 'Saved. Version bumped: seats must re-train.') : (es ? 'Guardado.' : 'Saved.'));
      loadSeats();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };
  // Agency owner reverts to the generic default (drops their override).
  const revertToGeneric = async () => {
    setSaving(true); setErr(null); setOkMsg(null);
    try {
      await updateAgencySettings({ hipaaTraining: { override: false } });
      const { config } = await getHipaaConfig();
      setOverriding(false); setCfg(norm(config));
      setOkMsg(es ? 'Volviste al contenido genérico de Deskte.' : 'Reverted to the generic Deskte content.');
      loadSeats();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const statusPill = (s) => {
    const map = {
      completed: { es: 'Completado', en: 'Completed', color: '#10b981' },
      expiring: { es: 'Por vencer', en: 'Expiring', color: '#f59e0b' },
      pending: { es: 'Pendiente', en: 'Pending', color: '#f59e0b' },
      expired: { es: 'Vencido', en: 'Expired', color: '#ef4444' },
      outdated: { es: 'Versión vieja', en: 'Outdated', color: '#ef4444' },
    };
    const m = map[s.status] || map.pending;
    return <span style={{ fontSize: '.7rem', fontWeight: 800, color: m.color, background: `${m.color}1a`, border: `1px solid ${m.color}55`, borderRadius: 100, padding: '.18rem .55rem' }}>{es ? m.es : m.en}</span>;
  };
  const fmt = (d) => { try { return d ? new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—'; } catch { return '—'; } };
  // Open a seat's uploaded certificate via a short-lived signed URL.
  const openCert = async (ackId) => {
    try { const url = await getHipaaCertUrl(ackId, { download: true }); if (url) window.open(url, '_blank', 'noopener'); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ padding: '1.2rem 1.4rem', maxWidth: 920, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
        <ShieldCheck size={22} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.15rem', color: 'var(--c-white,#eef6ff)', fontWeight: 800 }}>{es ? 'Capacitación HIPAA de Seats' : 'HIPAA Seat Training'}</h1>
          <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>
            {isPlatformOwner ? (es ? 'Contenido genérico que heredan TODAS las agencias.' : 'Generic content inherited by ALL agencies.') : (es ? 'Configura el entrenamiento y revisa quién lo completó.' : 'Configure the training and review who completed it.')}
          </div>
        </div>
      </div>

      <div style={{ fontSize: '.76rem', color: 'var(--c-amber,#ffb700)', background: 'rgba(255,183,0,.08)', border: '1px solid rgba(255,183,0,.25)', borderRadius: 8, padding: '.6rem .8rem', marginBottom: '1rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{es ? 'El curso y el texto del acuerdo deben ser aprobados por un abogado/a de HIPAA. Cada agencia paga el curso directo al proveedor. Deskte solo guarda quién lo completó, su certificado y cuándo.' : 'The course and agreement text must be approved by HIPAA counsel. Each agency pays the course provider directly. Deskte only records who completed it, their certificate and when.'}</span>
      </div>

      {/* Agency mode: inheriting vs customizing banner */}
      {!isPlatformOwner && (
        <div style={{ fontSize: '.78rem', color: overriding ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7da0c4)', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.6rem .8rem', marginBottom: '1rem', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span>{overriding ? (es ? '✏️ Estás usando tu propio contenido (personalizado).' : '✏️ You are using your own (customized) content.') : (es ? '✅ Estás usando el contenido genérico de Deskte. No tienes que hacer nada.' : '✅ You are using the generic Deskte content. Nothing to do.')}</span>
          {overriding
            ? <button onClick={revertToGeneric} disabled={saving} style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 8, padding: '.35rem .7rem', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer' }}>{es ? 'Volver al genérico' : 'Use generic'}</button>
            : <button onClick={() => setOverriding(true)} style={{ background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 8, padding: '.35rem .7rem', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer' }}>{es ? 'Personalizar para mi agencia' : 'Customize for my agency'}</button>}
        </div>
      )}

      {/* Renewal alerts — seats whose certificate is expiring (≤90d) or expired,
          mirroring the Licenses module. Informational: the seat must re-train. */}
      {(() => {
        const fmtDays = (d) => d < 0 ? (es ? `venció hace ${Math.abs(d)}d` : `expired ${Math.abs(d)}d ago`)
          : d === 0 ? (es ? 'vence HOY' : 'expires TODAY')
          : d === 1 ? (es ? 'vence MAÑANA' : 'expires TOMORROW')
          : (es ? `vence en ${d} días` : `expires in ${d} days`);
        const alerting = seats
          .filter((s) => s.daysLeft != null && s.daysLeft <= 90)
          .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0));
        if (alerting.length === 0) return null;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
            {alerting.map((s) => {
              const expired = s.daysLeft < 0;
              const color = expired ? '#ef4444' : '#ffb700';
              return (
                <div key={s.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, background: `${color}14`, border: `1px solid ${color}40`, borderRadius: 9, padding: '.55rem .8rem' }}>
                  <Clock size={15} style={{ color, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: '.8rem', color: 'var(--c-text,#c8dff0)' }}>
                    <strong>{s.name}</strong>{s.agencyName ? ` (${s.agencyName})` : ''} — {es ? 'capacitación HIPAA' : 'HIPAA training'} {fmtDays(s.daysLeft)} {s.expiresAt ? `(${fmt(s.expiresAt)})` : ''}
                  </div>
                  {s.hasCert && s.ackId && (
                    <button onClick={() => openCert(s.ackId)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 7, padding: '.28rem .5rem', fontSize: '.68rem', fontWeight: 700, cursor: 'pointer' }}>
                      <FileText size={12} /> {es ? 'Certificado' : 'Certificate'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Config */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--c-surface,#0d1f33)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: editable ? 'pointer' : 'default', opacity: editable ? 1 : 0.6 }}>
          <input type="checkbox" checked={cfg.enabled} disabled={!editable} onChange={(e) => set('enabled', e.target.checked)} style={{ width: 18, height: 18, accentColor: '#00d4ff', cursor: editable ? 'pointer' : 'default' }} />
          <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{es ? 'Exigir la capacitación a los seats' : 'Require training for seats'}</span>
        </label>

        {/* Course link (the external paid course) — bilingual, OPTIONAL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={lbl}>🇺🇸 {es ? 'Link del curso — Inglés (opcional)' : 'Course link — English (optional)'}</label>
            <input value={cfg.courseUrl} disabled={!editable} onChange={(e) => set('courseUrl', e.target.value)} placeholder="https://… (HIPAA training course)" style={{ ...inp, opacity: editable ? 1 : 0.6 }} />
          </div>
          <div>
            <label style={lbl}>🇪🇸 {es ? 'Link del curso — Español (opcional)' : 'Course link — Spanish (optional)'}</label>
            <input value={cfg.courseUrlEs} disabled={!editable} onChange={(e) => set('courseUrlEs', e.target.value)} placeholder="https://… (curso HIPAA)" style={{ ...inp, opacity: editable ? 1 : 0.6 }} />
          </div>
          <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)' }}>
            {es
              ? 'El link del curso es opcional: es solo una sugerencia. La agencia puede usar otro curso si prefiere. Lo único obligatorio es que el seat suba su certificado de capacitación HIPAA.'
              : 'The course link is optional — just a suggestion. The agency may use a different course if they prefer. The only requirement is that the seat uploads their HIPAA training certificate.'}
          </div>
        </div>

        {/* Why text */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>{es ? '¿Por qué hacerlo? (texto para el seat)' : 'Why do it? (text for the seat)'}</label>
            {editable && !cfg.whyText && (
              <button type="button" onClick={() => set('whyText', draftWhy(es))} style={{ background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 6, padding: '.15rem .5rem', fontSize: '.66rem', fontWeight: 700, cursor: 'pointer' }}>{es ? 'Cargar borrador' : 'Load draft'}</button>
            )}
          </div>
          <textarea value={cfg.whyText} disabled={!editable} onChange={(e) => set('whyText', e.target.value)} rows={3} placeholder={draftWhy(es)} style={{ ...inp, resize: 'vertical', opacity: editable ? 1 : 0.6 }} />
        </div>

        {/* Instructions */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>{es ? 'Instrucciones (pasos para el seat)' : 'Instructions (steps for the seat)'}</label>
            {editable && !cfg.instructions && (
              <button type="button" onClick={() => set('instructions', draftSteps(es))} style={{ background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 6, padding: '.15rem .5rem', fontSize: '.66rem', fontWeight: 700, cursor: 'pointer' }}>{es ? 'Cargar borrador' : 'Load draft'}</button>
            )}
          </div>
          <textarea value={cfg.instructions} disabled={!editable} onChange={(e) => set('instructions', e.target.value)} rows={4} placeholder={draftSteps(es)} style={{ ...inp, resize: 'vertical', opacity: editable ? 1 : 0.6 }} />
        </div>

        {/* Require certificate toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: editable ? 'pointer' : 'default', opacity: editable ? 1 : 0.6 }}>
          <input type="checkbox" checked={cfg.requireCert} disabled={!editable} onChange={(e) => set('requireCert', e.target.checked)} style={{ width: 18, height: 18, accentColor: '#00d4ff', cursor: editable ? 'pointer' : 'default' }} />
          <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--c-text,#c8dff0)' }}>{es ? 'Exigir que el seat suba su certificado' : 'Require the seat to upload their certificate'}</span>
        </label>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>{es ? 'Texto del acuerdo (aprobado por un abogado)' : 'Agreement text (approved by counsel)'}</label>
            {editable && !cfg.agreementText && (
              <button type="button" onClick={() => set('agreementText', draftAgreement(es))}
                style={{ background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.35)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 6, padding: '.15rem .5rem', fontSize: '.66rem', fontWeight: 700, cursor: 'pointer' }}>
                {es ? 'Cargar borrador' : 'Load draft'}
              </button>
            )}
          </div>
          <textarea value={cfg.agreementText} disabled={!editable} onChange={(e) => set('agreementText', e.target.value)} rows={8} placeholder={draftAgreement(es)} style={{ ...inp, resize: 'vertical', opacity: editable ? 1 : 0.6 }} />
          <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
            {es ? 'Si lo dejas vacío, se usa un texto genérico que se muestra en el idioma del seat (inglés o español).' : 'If you leave it empty, a generic text is used that shows in the seat\'s language (English or Spanish).'}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>{es ? 'Renovar cada (meses)' : 'Renew every (months)'}</label>
            <input type="number" min="0" value={cfg.renewMonths} disabled={!editable} onChange={(e) => set('renewMonths', e.target.value)} style={{ ...inp, opacity: editable ? 1 : 0.6 }} />
            <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>{es ? '0 = no caduca. 12 = anual.' : '0 = never expires. 12 = yearly.'}</div>
          </div>
          <div>
            <label style={lbl}>{es ? 'Versión actual' : 'Current version'}</label>
            <div style={{ ...inp, display: 'flex', alignItems: 'center', color: 'var(--c-cyan,#00d4ff)', fontWeight: 800 }}>v{cfg.version}</div>
          </div>
        </div>
        {err && <div style={{ fontSize: '.78rem', color: 'var(--c-red,#ff3d6b)' }}>{err}</div>}
        {okMsg && <div style={{ fontSize: '.78rem', color: 'var(--c-green,#00ff88)' }}>{okMsg}</div>}
        {editable && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button onClick={() => save(true)} disabled={saving} title={es ? 'Guarda y obliga a re-capacitarse' : 'Save and force re-training'}
              style={{ background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-text2,#7aa3c0)', borderRadius: 9, padding: '.55rem 1rem', fontSize: '.8rem', fontWeight: 700, cursor: 'pointer' }}>
              {es ? 'Guardar + nueva versión' : 'Save + new version'}
            </button>
            <button onClick={() => save(false)} disabled={saving}
              style={{ background: 'var(--c-cyan,#00d4ff)', border: 'none', color: '#0a1628', borderRadius: 9, padding: '.55rem 1.2rem', fontSize: '.82rem', fontWeight: 800, cursor: saving ? 'wait' : 'pointer' }}>
              {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
            </button>
          </div>
        )}
      </div>

      {/* Audit list */}
      <div style={{ marginTop: '1.4rem' }}>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
          {es ? `Estado de los seats (${seats.length})` : `Seat status (${seats.length})`}
        </div>
        {seats.length === 0 && (
          <div style={{ fontSize: '.82rem', color: 'var(--c-text3,#45688a)', padding: '.5rem 0' }}>
            {isPlatformOwner ? (es ? 'Ningún seat en la plataforma todavía.' : 'No seats on the platform yet.') : (es ? 'No tienes seats todavía.' : 'No seats yet.')}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {seats.map((s) => (
            <div key={s.userId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.55rem .75rem', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 9 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>
                  {s.name}
                  {s.agencyName && <span style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--c-text3,#45688a)', marginLeft: 6 }}>· {s.agencyName}</span>}
                </div>
                <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>
                  {s.completedAt ? `${es ? 'Firmó' : 'Signed'}: ${s.signatureName || '—'} · ${fmt(s.completedAt)}${s.expiresAt ? ` · ${es ? 'vence' : 'expires'} ${fmt(s.expiresAt)}` : ''}` : (es ? 'Sin completar' : 'Not completed')}
                </div>
              </div>
              {s.hasCert && s.ackId && (
                <button onClick={() => openCert(s.ackId)} title={es ? 'Ver certificado' : 'View certificate'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', borderRadius: 7, padding: '.3rem .55rem', fontSize: '.7rem', fontWeight: 700, cursor: 'pointer' }}>
                  <FileText size={13} /> {es ? 'Certificado' : 'Certificate'}
                </button>
              )}
              {statusPill(s)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
